import React from "react";

import {
  FetchNotFoundError,
  FetchServerError,
  FetchUnauthorizedError,
  FetchPasswordInvalidError,
  FetchSsoError,
  FetchElevatedAccessError,
} from "@lib/errors";
import { FetchUserError } from "@lib/errors/FetchUserError";
import { UNIDENTIFIED_ERROR } from "@lib/fetch";

import useI18nNamespace from "@services/useI18nNamespace";

import { getBrazeApiDenialCode } from "./utils/getBrazeApiDenialCode";
import type { FetchErrorModalTitleProps } from "./types";

export const FetchErrorModalTitle = ({ error }: FetchErrorModalTitleProps): React.ReactElement => {
  const { t } = useI18nNamespace("server-fetch-error-messages");
  const denialCode = getBrazeApiDenialCode(error);
  // Braze::API denial codes pick a truthful title before the class branches — see the matching
  // switch in FetchErrorModalBody.
  switch (denialCode) {
    case "forbidden.csrf":
      return <>{t("csrf-token-expired-title", "Session Expired")}</>;
    case "forbidden.read_only_impersonation":
      return <>{t("read-only-impersonation-title", "Read-Only Impersonation")}</>;
    case "unauthorized.ip_not_allowed":
    case "unauthorized.company_banned":
      return <>{t("account-restricted-title", "Account Issue")}</>;
    case "unauthorized.credential_type_not_accepted":
      return <>{t("server-error-title", "Unexpected Error")}</>;
    case "sandbox_tos.required":
      return <>{t("sandbox-tos-required-title", "Terms of Service Required")}</>;
    case "service_unavailable.company":
      return <>{t("account-unavailable-title", "Account Temporarily Unavailable")}</>;
  }
  if (error instanceof FetchServerError) {
    switch (error.status) {
      case 503:
      case 504:
        return <>{t("request-timeout-title", "Request Timed Out")}</>;
      case 500:
      default:
        return <>{t("server-error-title", "Unexpected Error")}</>;
    }
  }
  if (error instanceof FetchElevatedAccessError) {
    return <>{t("fetch-elevated-access-error-title", "Reauthentication Required")}</>;
  }
  if (error instanceof FetchUserError) {
    return <>{error.responseJson.warn_header}</>;
  }
  if (error instanceof FetchNotFoundError) {
    return <>{t("fetch-not-found-title", "Page Not Found")}</>;
  }
  if (error instanceof FetchUnauthorizedError) {
    return <>{t("unauthorized-title", "Insufficient Permissions")}</>;
  }
  if (error instanceof FetchPasswordInvalidError) {
    return <>{t("password-expired-or-invalid-title", "Must Change Password")}</>;
  }
  if (error instanceof FetchSsoError) {
    return <>{t("sso-title", "SSO Authentication Now Required")}</>;
  }
  // This refresh title is for ApplicationController#render_409, which a reload fixes. Many legacy endpoints also
  // send a JSON 409, so every 409 without the Braze::API envelope keeps this title. A Braze::API 409 carries a
  // domain error_code that a reload cannot fix, so it gets the generic title below, like an enveloped 400 or 422.
  if (error.status === UNIDENTIFIED_ERROR && denialCode === undefined) {
    return <>{t("unidentified-error-title", "Please Refresh Page")}</>;
  }
  return <>{t("server-error-title", "Unexpected Error")}</>;
};
